import { useState } from "react";
import axios from "axios";
import { API } from "../../config";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  Search, Loader2, CheckCircle, AlertTriangle, XCircle,
  TrendingUp, FileText, Sparkles, BarChart3
} from "lucide-react";

const FACTOR_LABELS = {
  title_score: "Title Optimization",
  keyword_usage: "Keyword Usage",
  content_structure: "Content Structure",
  ai_citation_ready: "AI Citation Ready",
  schema_potential: "Schema Markup",
  internal_linking: "Internal Linking",
  meta_description: "Meta Description",
  uniqueness: "Content Uniqueness",
};

const GRADE_COLORS = {
  "A+": "text-emerald-400 bg-emerald-900/30 border-emerald-700",
  "A": "text-emerald-400 bg-emerald-900/30 border-emerald-700",
  "B+": "text-green-400 bg-green-900/30 border-green-700",
  "B": "text-green-400 bg-green-900/30 border-green-700",
  "C+": "text-amber-400 bg-amber-900/30 border-amber-700",
  "C": "text-amber-400 bg-amber-900/30 border-amber-700",
  "D": "text-orange-400 bg-orange-900/30 border-orange-700",
  "F": "text-red-400 bg-red-900/30 border-red-700",
};

export default function SEOScoreTab({ user }) {
  const { t } = useLanguage();
  const [title, setTitle] = useState("");
  const [keyword, setKeyword] = useState("");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [score, setScore] = useState(null);
  const [error, setError] = useState("");

  const handleScore = async () => {
    if (!title.trim()) return;
    setLoading(true);
    setError("");
    setScore(null);
    try {
      const res = await axios.post(`${API}/seo-score`, {
        title: title.trim(),
        target_keyword: keyword.trim(),
        content: content.trim(),
        language: "en",
      });
      setScore(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Error occurred");
    } finally {
      setLoading(false);
    }
  };

  const getBarColor = (v) => {
    if (v >= 80) return "bg-emerald-500";
    if (v >= 60) return "bg-green-500";
    if (v >= 40) return "bg-amber-500";
    return "bg-purple-500";
  };

  return (
    <div className="space-y-6" data-testid="seo-score-tab">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-black font-['Cabinet_Grotesk'] tracking-tight" data-testid="seo-score-title">
          {t.ss_title || "SEO/GEO Score"}
        </h2>
        <p className="text-zinc-500 text-sm mt-1">
          {t.ss_desc || "Score your content before publishing — optimize for Google and AI search engines"}
        </p>
      </div>

      {/* Input Form */}
      <div className="bg-[#141414] border border-zinc-800 p-6 space-y-4" data-testid="seo-score-form">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-zinc-400 font-['JetBrains_Mono'] uppercase tracking-wider mb-1 block">
              {t.ss_article_title || "Article Title"}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t.ss_title_placeholder || "Enter your article title..."}
              className="w-full px-4 py-3 bg-[#0a0a0a] border border-zinc-700 text-white focus:border-[#a855f7] focus:outline-none text-sm"
              data-testid="seo-title-input"
            />
          </div>
          <div>
            <label className="text-xs text-zinc-400 font-['JetBrains_Mono'] uppercase tracking-wider mb-1 block">
              {t.ss_keyword || "Target Keyword"}
            </label>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder={t.ss_keyword_placeholder || "e.g. best crm for startups"}
              className="w-full px-4 py-3 bg-[#0a0a0a] border border-zinc-700 text-white focus:border-[#a855f7] focus:outline-none text-sm"
              data-testid="seo-keyword-input"
            />
          </div>
        </div>
        <div>
          <label className="text-xs text-zinc-400 font-['JetBrains_Mono'] uppercase tracking-wider mb-1 block">
            {t.ss_content || "Content"} <span className="text-zinc-600">({t.ss_optional || "optional"})</span>
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={4}
            placeholder={t.ss_content_placeholder || "Paste your article content here for a more accurate score..."}
            className="w-full px-4 py-3 bg-[#0a0a0a] border border-zinc-700 text-white focus:border-[#a855f7] focus:outline-none text-sm resize-y"
            data-testid="seo-content-input"
          />
        </div>
        <button
          onClick={handleScore}
          disabled={loading || !title.trim()}
          className="px-6 py-3 bg-[#a855f7] text-white font-bold uppercase tracking-wider hover:bg-purple-600 transition-colors disabled:opacity-50 flex items-center gap-2 text-sm"
          data-testid="seo-score-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
          {loading ? (t.ss_scoring || "Scoring...") : (t.ss_calculate || "Calculate Score")}
        </button>
        {error && <p className="text-red-400 text-sm">{error}</p>}
      </div>

      {/* Results */}
      {score && (
        <div className="space-y-4" data-testid="seo-score-results">
          {/* Overall score card */}
          <div className="bg-[#141414] border border-zinc-800 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-xs text-zinc-500 font-['JetBrains_Mono'] uppercase tracking-wider mb-1">
                  {t.ss_overall || "Overall Score"}
                </p>
                <p className="text-5xl font-black font-['Cabinet_Grotesk']" data-testid="overall-score-value">
                  <span className={score.overall_score >= 70 ? "text-emerald-400" : score.overall_score >= 50 ? "text-amber-400" : "text-red-400"}>
                    {score.overall_score}
                  </span>
                  <span className="text-zinc-600 text-2xl">/100</span>
                </p>
              </div>
              <div className={`w-20 h-20 flex items-center justify-center border-2 ${GRADE_COLORS[score.grade] || "text-zinc-400 bg-zinc-900/30 border-zinc-700"}`} data-testid="score-grade">
                <span className="text-3xl font-black font-['Cabinet_Grotesk']">{score.grade}</span>
              </div>
            </div>

            {/* Factor bars */}
            <div className="space-y-3">
              {Object.entries(FACTOR_LABELS).map(([key, label]) => {
                const val = score[key] || 0;
                return (
                  <div key={key} className="flex items-center gap-3" data-testid={`factor-${key}`}>
                    <span className="text-xs text-zinc-400 w-32 flex-shrink-0 font-['JetBrains_Mono']">{t[`ss_f_${key}`] || label}</span>
                    <div className="flex-1 h-2 bg-zinc-800 overflow-hidden">
                      <div className={`h-full ${getBarColor(val)} transition-all`} style={{ width: `${val}%` }} />
                    </div>
                    <span className="text-xs text-zinc-300 font-['JetBrains_Mono'] w-8 text-right">{val}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Improvements & Strengths */}
          <div className="grid md:grid-cols-2 gap-4">
            {score.top_3_improvements?.length > 0 && (
              <div className="bg-[#141414] border border-zinc-800 p-5" data-testid="score-improvements">
                <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  {t.ss_improvements || "Improvements"}
                </h3>
                <div className="space-y-2">
                  {score.top_3_improvements.map((tip, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-zinc-400">
                      <span className="text-amber-400 font-bold flex-shrink-0">{i + 1}.</span>
                      <span>{tip}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {score.strengths?.length > 0 && (
              <div className="bg-[#141414] border border-zinc-800 p-5" data-testid="score-strengths">
                <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  {t.ss_strengths || "Strengths"}
                </h3>
                <div className="space-y-2">
                  {score.strengths.map((s, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-zinc-400">
                      <CheckCircle className="w-3 h-3 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span>{s}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!score && !loading && (
        <div className="bg-[#141414] border border-zinc-800 p-12 text-center">
          <BarChart3 className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
          <h3 className="text-white font-bold font-['Cabinet_Grotesk'] text-lg mb-2">
            {t.ss_empty_h || "Score your content before publishing"}
          </h3>
          <p className="text-zinc-500 text-sm max-w-md mx-auto">
            {t.ss_empty_desc || "Enter your article title and content to get an AI-powered SEO/GEO score with actionable improvement tips."}
          </p>
        </div>
      )}
    </div>
  );
}
